/**
 * Turning `kw_ranks` rows into the keyword-first grid the explorer draws.
 *
 * The app detail panel asks "where does THIS app rank"; this asks the
 * transpose — for one keyword, which apps hold which positions, and how that
 * moved day by day. The table is already the right shape:
 * `(app_store_id, keyword_id, observed_on, rank_position)` is keyword x day x
 * app x position with no schema change.
 *
 * WHAT THE DATA ACTUALLY HOLDS, measured against production on 2026-08-19:
 * 137,675 keywords exist, 10 have ever had a SERP fetched, and every one of
 * the 1,709 stored positions carries the same `observed_on` of 2026-08-12. So
 * there is exactly one day of history and a line chart would draw one dot.
 * `observedDays` returning a single entry is the normal case today, not an
 * error, and the caller is expected to say so rather than render a broken
 * chart. Once the sweep is scheduled the same functions draw the real line.
 *
 * Depth is cut at #25 because a SERP here goes to ~190 and the tail is noise:
 * of those 1,709 positions, 70.8% sit past #50. Cutting at 25 keeps a day
 * under 25 rows per keyword and keeps the chart readable; the cut is stated in
 * the UI rather than hidden, because an app that drops out of the top 25 also
 * drops out of its line.
 *
 * Pure on purpose. This repository's vitest runs in the node environment with
 * no jsdom, so logic that lives in a component cannot be pinned by a test.
 */

import { difficultyTier } from './keywords/keyword-scores';

/** The storefront a keyword row falls back to when its own is blank. */
export const COUNTRY_FALLBACK = 'us';

/** Deepest rank position the explorer reads. See the note above. */
export const EXPLORER_DEPTH = 25;

/** How many days of history a single keyword request looks back over. */
export const EXPLORER_DAYS = 30;

/** How many apps the chart draws at once before it becomes unreadable. */
export const MAX_CHART_APPS = 10;

export type RankObservation = {
  app_store_id: number;
  rank_position: number;
  observed_on: string;
};

export type SeriesPoint = { observed_on: string; rank_position: number | null };

/**
 * One line on the grid, whichever way the grid is read.
 *
 * The explorer asks "for this keyword, how did each app move" and the app
 * detail panel asks "for this app, how did each keyword move". Both are the
 * same shape — a run of positions over a day axis — so `movement` reads this
 * and not a keyword-shaped or app-shaped copy of it.
 */
export type RankSeries = {
  /** Position on the most recent day of the axis, or null if it was absent. */
  latest_rank: number | null;
  /** Position on the earliest day of the axis it appeared on. */
  first_rank: number | null;
  /** How many days of the axis it appeared on at all. */
  days_seen: number;
  /** One point per day of the axis, null where it was not in the top N. */
  points: SeriesPoint[];
};

export type AppSeries = RankSeries & {
  app_store_id: number;
  /** Best (numerically lowest) position this app held in the window. */
  best_rank: number;
};

/** How a delta reads once the two ends of the axis are known. */
export type MovementState = 'up' | 'down' | 'flat' | 'new' | 'gone' | 'none';

/** Every distinct day in the window, oldest first. */
export function observedDays(rows: RankObservation[] | null | undefined): string[] {
  const days = new Set<string>();
  for (const row of rows || []) {
    if (row && typeof row.observed_on === 'string' && row.observed_on) days.add(row.observed_on);
  }
  return Array.from(days).sort();
}

/**
 * A run of positions laid over a day axis that is given rather than derived.
 *
 * The axis matters more than it looks. A day an app is absent from has no
 * `kw_ranks` row, so an axis built from the rows themselves cannot tell "the
 * keyword was not swept that day" from "the app was not in the results" — and
 * that difference is exactly what "gone" means. The app detail panel therefore
 * passes the sweep days out of `kw_serps`; the explorer passes the days its own
 * rows carry, which for one keyword is the same set.
 */
export function seriesOverDays(
  days: string[],
  ranksByDay: Map<string, number>
): RankSeries {
  const points = days.map((observed_on) => ({
    observed_on,
    rank_position: ranksByDay.get(observed_on) ?? null,
  }));
  const latestDay = days.length > 0 ? days[days.length - 1] : null;
  const firstSeen = points.find((p) => p.rank_position !== null);
  return {
    latest_rank: latestDay ? ranksByDay.get(latestDay) ?? null : null,
    first_rank: firstSeen ? firstSeen.rank_position : null,
    days_seen: points.filter((p) => p.rank_position !== null).length,
    points,
  };
}

/**
 * One series per app, ordered the way the table reads: whoever holds the best
 * position on the latest day comes first, apps that have dropped out of the
 * window fall to the bottom in best-rank order.
 *
 * A day where an app was not in the top `EXPLORER_DEPTH` becomes a null point
 * rather than a missing one, so recharts leaves a gap in the line instead of
 * drawing a straight segment across a week the app was absent for.
 */
export function seriesByApp(rows: RankObservation[] | null | undefined): AppSeries[] {
  const days = observedDays(rows);
  const byApp = new Map<number, Map<string, number>>();

  for (const row of rows || []) {
    if (!row) continue;
    const id = Number(row.app_store_id);
    const rank = Number(row.rank_position);
    if (!Number.isFinite(id) || !Number.isFinite(rank) || rank <= 0) continue;
    let perDay = byApp.get(id);
    if (!perDay) {
      perDay = new Map<string, number>();
      byApp.set(id, perDay);
    }
    // Two rows for one app on one day should not happen, but if the sweep ever
    // writes both, the better position is the one that was really held.
    const existing = perDay.get(row.observed_on);
    if (existing === undefined || rank < existing) perDay.set(row.observed_on, rank);
  }

  const series: AppSeries[] = [];
  for (const [app_store_id, perDay] of byApp) {
    series.push({
      app_store_id,
      best_rank: Math.min(...Array.from(perDay.values())),
      ...seriesOverDays(days, perDay),
    });
  }

  series.sort((a, b) => {
    const al = a.latest_rank ?? Number.POSITIVE_INFINITY;
    const bl = b.latest_rank ?? Number.POSITIVE_INFINITY;
    if (al !== bl) return al - bl;
    if (a.best_rank !== b.best_rank) return a.best_rank - b.best_rank;
    return a.app_store_id - b.app_store_id;
  });
  return series;
}

/**
 * How far an app climbed across the window. Positive means it moved UP the
 * page — position 9 to position 4 is +5 — because a smaller rank is a better
 * one and a minus sign in front of an improvement reads as a loss.
 *
 * Null when there is nothing to compare: one observed day, or an app that is
 * missing from either end of the window.
 */
export function movement(series: RankSeries): number | null {
  if (series.days_seen < 2) return null;
  if (series.first_rank === null || series.latest_rank === null) return null;
  if (series.points.length < 2) return null;
  return series.first_rank - series.latest_rank;
}

/**
 * The same comparison, read as a word instead of a number.
 *
 * It splits the null `movement` returns, because "—" for an app that has just
 * arrived and "—" for an app that has just dropped out are the same mark for
 * opposite news, and at a depth cut of 25 both are the common case. The ends of
 * the axis are what decide it, not the days the app happens to appear on:
 * `first_rank` is the first day it was SEEN, so it cannot tell arrival from
 * presence on its own.
 */
export function movementState(series: RankSeries): MovementState {
  const { points } = series;
  if (points.length < 2) return 'none';
  const before = points[0].rank_position;
  const after = points[points.length - 1].rank_position;
  if (before === null && after === null) return 'none';
  if (before === null) return 'new';
  if (after === null) return 'gone';
  if (before === after) return 'flat';
  return before > after ? 'up' : 'down';
}

/** The delta as a person reads it. Empty string when there is no movement to show. */
export function movementLabel(delta: number | null): string {
  if (delta === null) return '';
  if (delta === 0) return 'no change';
  return delta > 0 ? `up ${delta}` : `down ${Math.abs(delta)}`;
}

/**
 * The apps that MOVED most across the window, biggest mover first.
 *
 * The chart and the table below it deliberately show different populations:
 * the chart draws the ten apps whose position changed most, and the table
 * lists every app the keyword has ever put on a page. Ten flat lines held by
 * whoever happens to sit at the top is a picture of nothing; ten lines that
 * crossed is the picture someone opened the page for.
 *
 * Direction is not the sort key, size is — a fall of 9 is as much news as a
 * climb of 9. An app with no comparable pair scores below every app that has
 * one, so a keyword with a single observed day falls back to latest-rank
 * order rather than to an arbitrary one.
 */
export function topMovers(series: AppSeries[], limit: number = MAX_CHART_APPS): AppSeries[] {
  const scored = series.map((s) => {
    const delta = movement(s);
    return { s, size: delta === null ? -1 : Math.abs(delta) };
  });
  scored.sort((a, b) => {
    if (a.size !== b.size) return b.size - a.size;
    const al = a.s.latest_rank ?? Number.POSITIVE_INFINITY;
    const bl = b.s.latest_rank ?? Number.POSITIVE_INFINITY;
    if (al !== bl) return al - bl;
    return a.s.app_store_id - b.s.app_store_id;
  });
  return scored.slice(0, limit).map((entry) => entry.s);
}

/**
 * One day's search page, best position first, in the order it really stood
 * that day. Apps that held no position that day sort to the bottom.
 *
 * This is what the table is ordered by, and the day it is given moves as the
 * reader scrubs. Nothing is pinned above it — a row held at the top would be
 * telling the reader that day's page had it there when it did not.
 */
export function orderForDay(series: AppSeries[], dayIndex: number): AppSeries[] {
  return [...series].sort((a, b) => {
    const ar = a.points[dayIndex]?.rank_position ?? Number.POSITIVE_INFINITY;
    const br = b.points[dayIndex]?.rank_position ?? Number.POSITIVE_INFINITY;
    if (ar !== br) return ar - br;
    if (a.best_rank !== b.best_rank) return a.best_rank - b.best_rank;
    return a.app_store_id - b.app_store_id;
  });
}

/**
 * The day index a pointer or a key event is standing on, read off the DOM.
 *
 * The chart hands the index over as a STRING — a `data-day` attribute, and
 * before that recharts' activeTooltipIndex, which is where this bit first: a
 * `typeof === "number"` guard silently never fired, the tooltip followed the
 * pointer and the table stayed on the pinned day (commit b8a864d). Parse it
 * rather than trust its type, and refuse anything that is not a day the
 * series actually has: `-1`, `1.5`, `"abc"` and an index past the newest
 * day are all null, so a stale attribute can never point the table at a day
 * that is not there.
 */
export function heldDayIndex(raw: unknown, dayCount: number): number | null {
  const i = typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : raw;
  if (typeof i !== 'number' || !Number.isInteger(i)) return null;
  return i >= 0 && i < dayCount ? i : null;
}

/**
 * How far an app moved INTO one day: its position on the day before, minus
 * its position on this one. Positive is a climb, the same sign convention
 * `movement` uses over the whole window.
 *
 * The table needs this rather than `movement` because the table is scrubbed
 * to a day. A window-wide delta printed beside a single day's position
 * answers a question the reader did not ask, and reads as wrong the moment
 * the two disagree. Null when either end is missing: the oldest day has
 * nothing before it, and an app absent from either day has no position to
 * subtract.
 */
export function movementInto(series: RankSeries, dayIndex: number): number | null {
  const before = series.points[dayIndex - 1]?.rank_position ?? null;
  const after = series.points[dayIndex]?.rank_position ?? null;
  if (before === null || after === null) return null;
  return before - after;
}

/**
 * The sentence under the chart. It has to be honest about a single-day window,
 * because that is what production holds today and a chart with one dot on it
 * otherwise reads as broken rather than as early.
 */
export function historyNote(days: string[]): string {
  if (days.length === 0) return 'No search results have been recorded for this keyword yet.';
  if (days.length === 1) {
    return `One observation, from ${days[0]}. Daily tracking is not scheduled yet, so there is no movement to draw.`;
  }
  return `${days.length} days observed, ${days[0]} to ${days[days.length - 1]}.`;
}

/**
 * The same word as `movementState`, but for ONE day of the axis rather than
 * the whole window.
 *
 * The table is scrubbed to a day, so the word beside a position has to describe
 * that day's step. `movementState` compares the two ENDS of the axis, which is
 * a different question and reads as wrong the moment the reader holds a middle
 * day. Day 0 has nothing before it: that is 'none', and the caller says which
 * silence it is by passing `firstDay` to the renderer.
 */
export function movementStateInto(series: RankSeries, dayIndex: number): MovementState {
  if (dayIndex <= 0) return 'none';
  const before = series.points[dayIndex - 1]?.rank_position ?? null;
  const after = series.points[dayIndex]?.rank_position ?? null;
  if (before === null && after === null) return 'none';
  if (before === null) return 'new';
  if (after === null) return 'gone';
  if (before === after) return 'flat';
  return before > after ? 'up' : 'down';
}

/**
 * How much a whole KEYWORD moved between its two most recent sweeps: the sum
 * of every app's absolute change in position across the top `EXPLORER_DEPTH`.
 *
 * This is the ordering the picker uses, and it replaces `popularity_score`,
 * which is a constant on this page. Measured on 2026-08-23: all 10 displayable
 * keywords carry the identical popularity 52,069,957, because the column
 * describes the app that PROPOSED the keyword and every one of the ten was
 * proposed by the same app. Movement is the one signal in this data with real
 * variation — ten well-separated values over the same ten keywords.
 *
 * Only apps that held a position on BOTH days count. An app that arrived or
 * dropped out has no pair to subtract, and scoring it as its own rank would
 * make a keyword look busy for a depth-cut artefact. Null when the keyword has
 * fewer than two observed days, which is not zero: zero means nothing moved
 * and null means nothing can be known yet.
 */
export function keywordMovementScore(rows: RankObservation[] | null | undefined): number | null {
  const inDepth = (rows || []).filter(
    (row) => row && Number.isFinite(Number(row.rank_position)) && Number(row.rank_position) > 0
      && Number(row.rank_position) <= EXPLORER_DEPTH
  );
  const days = observedDays(inDepth);
  if (days.length < 2) return null;

  const latest = days[days.length - 1];
  const previous = days[days.length - 2];
  const before = new Map<number, number>();
  const after = new Map<number, number>();
  for (const row of inDepth) {
    const id = Number(row.app_store_id);
    if (!Number.isFinite(id)) continue;
    const rank = Number(row.rank_position);
    if (row.observed_on === previous) {
      const seen = before.get(id);
      if (seen === undefined || rank < seen) before.set(id, rank);
    } else if (row.observed_on === latest) {
      const seen = after.get(id);
      if (seen === undefined || rank < seen) after.set(id, rank);
    }
  }

  let total = 0;
  for (const [id, was] of before) {
    const now = after.get(id);
    if (now !== undefined) total += Math.abs(was - now);
  }
  return total;
}

/**
 * Which keywords wear the fire: the top quarter of the list by movement score,
 * and only when they actually moved.
 *
 * Computed over the WHOLE observed set and never over a search-filtered view,
 * so the badge means the same thing whatever the reader has typed. A keyword
 * with no score yet — one observed day — is not in the running at all.
 *
 * All-equal scores badge nothing, and that is the point rather than an edge
 * case. A fire on every row distinguishes nothing, and this page has already
 * shipped one all-equal number (`popularity_score`) that read as a ranking and
 * was not one. The same guard makes a one-keyword list badge nothing.
 */
export function hotKeywords(entries: { id: string; movement: number | null }[]): Set<string> {
  const scored = entries.filter((e) => e.movement !== null);
  const n = scored.length;
  if (n === 0) return new Set<string>();

  const sorted = scored.map((e) => e.movement as number).sort((a, b) => b - a);
  const cut = Math.max(1, Math.floor(n / 4));
  const threshold = sorted[cut - 1];
  // Nothing moved, or everything is level with the cut so there is no top
  // quarter to point at.
  if (threshold <= 0 || sorted[n - 1] >= threshold) return new Set<string>();
  return new Set(scored.filter((e) => (e.movement as number) >= threshold).map((e) => e.id));
}

/**
 * The `[from, to]` pairs that cover `count` rows in pages of `pageSize`, so a
 * paged PostgREST read can ask for every page at once. One round trip from the
 * app box is 108 ms (measured 2026-09-11), and paging in series paid it per
 * page. Capped at `maxPages`; the caller warns when the cap bites.
 */
export function pageRanges(count: number, pageSize: number, maxPages: number): [number, number][] {
  if (!(pageSize > 0) || !(count > 0)) return [];
  const pages = Math.min(Math.ceil(count / pageSize), Math.max(0, maxPages));
  const out: [number, number][] = [];
  for (let page = 0; page < pages; page++) {
    out.push([page * pageSize, page * pageSize + pageSize - 1]);
  }
  return out;
}

/**
 * The picker's search, shared by the route (which still honours `q`) and the
 * hook (which filters the list it already holds, so typing sends nothing).
 * Under two characters the whole list comes back.
 */
export function filterByTerm<T extends { keyword: string }>(items: T[], rawTerm: string): T[] {
  const term = (rawTerm || '').slice(0, 80).trim().toLowerCase();
  if (term.length < 2) return items;
  return items.filter((item) => item.keyword.toLowerCase().includes(term));
}

/**
 * How many keyword rows the landing table asks for at a time. Measured on the
 * 4302 preview 2026-09-21: the whole 200-row list drew 7,027 elements and
 * 1,067 images in one go, and the table showed its first row after 5.2 s at
 * 390 and 6.3 s at 1440. A screen holds 10 to 15 rows, so 50 is three to five
 * screens, and the next 50 load as the reader scrolls.
 */
export const KEYWORD_PAGE = 50;

export const DIFFICULTY_BANDS = ['any', 'easy', 'moderate', 'hard', 'very hard'] as const;
export type DifficultyBand = (typeof DIFFICULTY_BANDS)[number];

/**
 * Whether a row sits in the chosen band. The names come from `difficultyTier`,
 * so the select and the bar cannot disagree about where "hard" starts; a row
 * with no score is in no band but "any". An unknown band reads as "any".
 */
export function inBand(row: { difficulty: { score: number } | null }, band: string): boolean {
  if (!(DIFFICULTY_BANDS as readonly string[]).includes(band) || band === 'any') return true;
  if (!row.difficulty) return false;
  return difficultyTier(row.difficulty.score).label.toLowerCase() === band;
}

/**
 * The two trending cards' orders. A null movement is "swept once", which is
 * not a zero, so it sorts below every keyword that has a real figure rather
 * than tying with "did not move". The route cuts both over the whole list and
 * the card sorts what it is handed, so they share these.
 */
export function biggestMovers<T extends { movement: number | null }>(rows: T[], limit: number): T[] {
  return [...rows].sort((a, b) => (b.movement ?? -1) - (a.movement ?? -1)).slice(0, limit);
}

export function mostTracked<T extends { tracked_by: number; apps_seen: number | null }>(
  rows: T[],
  limit: number
): T[] {
  return [...rows]
    .sort((a, b) => b.tracked_by - a.tracked_by || (b.apps_seen ?? 0) - (a.apps_seen ?? 0))
    .slice(0, limit);
}

/**
 * How many of five dots Apple's popularity lights for one keyword.
 *
 * `kw_hints.rank_at_term` is the keyword's place inside the ten suggestions
 * Apple returns for its own prefix, so ten places fold onto five dots two at a
 * time and a keyword Apple suggests at all keeps at least one lit. Null is no
 * suggestion, which is drawn as a dash rather than as one dot.
 *
 * It lives here and not in `keyword-cells.tsx` because a table row and a facts
 * row light their dots from the same number, and this repository's vitest runs
 * in the node environment with no jsdom: a mapping inside a component cannot be
 * pinned by a test, and two copies of it drift silently.
 */
export function popularityDots(rankAtTerm: number | null): number {
  if (rankAtTerm === null) return 0;
  return Math.min(5, Math.max(1, 5 - Math.floor((rankAtTerm - 1) / 2)));
}

/**
 * Keyword rows in the order both keyword tables show them: best position first,
 * unranked last, ties settled by the keyword itself.
 *
 * An unranked row sorts to the bottom rather than to the top, because a null
 * rank is "we hold no position for this", not position zero. The keyword breaks
 * the tie so the order is stable: several keywords share one position, and an
 * order that depends on the read's row order moves under the reader between
 * sweeps.
 *
 * One comparator for two callers — the app page's Search positions card and its
 * Tracked keywords table, which the spec requires to agree.
 */
export function byRankThenKeyword<T extends { rank: number | null; keyword: string }>(
  rows: T[],
): T[] {
  return [...rows].sort((a, b) => {
    const ar = a.rank ?? Number.POSITIVE_INFINITY;
    const br = b.rank ?? Number.POSITIVE_INFINITY;
    if (ar !== br) return ar - br;
    return a.keyword.localeCompare(b.keyword);
  });
}

/** One rank band of a keyword's grid, as `/api/keywords/ranks` returns it. */
export type GridPage<K, A extends { app_store_id: number }> = {
  keyword: K;
  ranks: RankObservation[];
  apps: A[];
  depth: number;
  days: number;
  stale: boolean;
  unavailable?: boolean;
};

/**
 * Folds the bands an infinite scroll has loaded into the one grid the page
 * reads. Everything but the rows comes from the first band, which is the same
 * response the unpaged read gave; the ranks concatenate in band order; and an
 * app that crossed a band boundary inside the window sits in two bands, so
 * the apps are de-duplicated by App Store id.
 */
export function mergeGridPages<K, A extends { app_store_id: number }>(
  pages: GridPage<K, A>[]
): GridPage<K, A> | null {
  if (pages.length === 0) return null;
  const apps = new Map<number, A>();
  for (const page of pages) for (const app of page.apps) if (!apps.has(app.app_store_id)) apps.set(app.app_store_id, app);
  return { ...pages[0], ranks: pages.flatMap((page) => page.ranks), apps: Array.from(apps.values()) };
}
